/*
 * SNUGLER redesign: size outline and mattress-depth slider.
 * Needs snugler-core.js, which must load first.
 */
(() => {
  const SR = window.SnuglerRedesign;
  if (!SR || !SR.register || SR.modules.has('fit')) return;
  const { isReduced, finePointer, clamp, range, lerp, smooth, easeOut, easeBack, fmt, onScroll, whenVisible, store, selectVariant } = SR.util;

  /* ------------------------------------------------------------------ fit check */

  function animateSpring(state, target, onFrame) {
    state.target = target;
    if (state.running) return;
    state.running = true;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      const k = 170;
      const c = 16;
      const force = (state.target - state.value) * k - state.velocity * c;
      state.velocity += force * dt;
      state.value += state.velocity * dt;
      onFrame(state.value);
      if (Math.abs(state.target - state.value) < 0.002 && Math.abs(state.velocity) < 0.002) {
        state.value = state.target;
        onFrame(state.value);
        state.running = false;
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  function initFit(root) {
    const section = root.querySelector('[data-snx-fit]');
    if (!section || section.__snx) return;
    section.__snx = true;

    /* size outline */
    const sizePanel = section.querySelector('[data-snx-size]');
    if (sizePanel) {
      let data = { sizes: [], length: 80 };
      try {
        data = JSON.parse(sizePanel.querySelector('[data-snx-size-data]').textContent);
      } catch (e) {}
      const rect = sizePanel.querySelector('[data-snx-bed-rect]');
      const ghost = sizePanel.querySelector('[data-snx-bed-ghost]');
      const pillows = sizePanel.querySelectorAll('[data-snx-pillow]');
      const quilt = sizePanel.querySelector('[data-snx-quilt]');
      const dimW = sizePanel.querySelector('[data-snx-dim-w]');
      const dimH = sizePanel.querySelector('[data-snx-dim-h]');
      const readout = sizePanel.querySelector('[data-snx-dims]');
      const SCALE = 2.7; // svg units per inch
      const LENGTH = (data.length || 80) * SCALE;
      const TOP = 40;
      const CX = 210;
      const widths = (data.sizes || []).map((s) => Number(s.w) || 60);
      const maxW = Math.max(...widths, 60) * SCALE;

      const sizeFor = (title) => {
        const t = String(title || '').toLowerCase();
        return (data.sizes || []).find((s) => s.name && t.includes(String(s.name).toLowerCase())) || null;
      };

      const springState = { value: 60 * SCALE, velocity: 0, target: 60 * SCALE, running: false };
      function render(w) {
        const x = CX - w / 2;
        rect.setAttribute('x', fmt(x));
        rect.setAttribute('width', fmt(w));
        rect.setAttribute('height', fmt(LENGTH));
        const pw = (w - 48) / 2;
        pillows[0].setAttribute('x', fmt(x + 16));
        pillows[0].setAttribute('width', fmt(pw));
        pillows[1].setAttribute('x', fmt(x + 32 + pw));
        pillows[1].setAttribute('width', fmt(pw));
        let q = '';
        for (let i = 1; i < 4; i++) {
          const yy = TOP + 96 + i * 32;
          q += `M ${fmt(x + 10)} ${yy} L ${fmt(x + w - 10)} ${yy} `;
        }
        for (let i = 1; i < 4; i++) {
          const xx = x + (w / 4) * i;
          q += `M ${fmt(xx)} ${TOP + 104} L ${fmt(xx)} ${TOP + LENGTH - 10} `;
        }
        quilt.setAttribute('d', q);
        dimW.setAttribute('d', `M ${fmt(x)} ${TOP + LENGTH + 16} L ${fmt(x + w)} ${TOP + LENGTH + 16} M ${fmt(x)} ${TOP + LENGTH + 10} v 12 M ${fmt(x + w)} ${TOP + LENGTH + 10} v 12`);
        const hx = CX - maxW / 2 - 16;
        dimH.setAttribute('d', `M ${fmt(hx)} ${TOP} L ${fmt(hx)} ${fmt(TOP + LENGTH)} M ${fmt(hx - 6)} ${TOP} h 12 M ${fmt(hx - 6)} ${fmt(TOP + LENGTH)} h 12`);
      }

      ghost.setAttribute('x', fmt(CX - maxW / 2));
      ghost.setAttribute('width', fmt(maxW));
      ghost.setAttribute('height', fmt(LENGTH));

      function update(variant, animate) {
        const title = variant ? variant.title : '';
        const size = sizeFor(title);
        const w = (size ? Number(size.w) || 60 : 60) * SCALE;
        readout.textContent = size && size.dims ? `${size.name}: ${size.dims}` : size ? size.name : title;
        if (!animate || isReduced()) {
          springState.value = w;
          springState.velocity = 0;
          render(w);
        } else animateSpring(springState, w, render);
      }

      sizePanel.querySelectorAll('[data-snx-size-btn]').forEach((btn) =>
        btn.addEventListener('click', () => selectVariant(btn.dataset.variantId, sizePanel))
      );
      document.addEventListener('snx:variant', (e) => update(e.detail.variant, true));
      const pressed = sizePanel.querySelector('[data-snx-size-btn][aria-pressed="true"]');
      update(store.current || (pressed ? { title: pressed.dataset.variantTitle } : null), false);
    }

    /* depth slider */
    const depthPanel = section.querySelector('[data-snx-depth]');
    if (depthPanel) {
      const input = depthPanel.querySelector('[data-snx-depth-input]');
      const out = depthPanel.querySelector('[data-snx-depth-out]');
      const verdict = depthPanel.querySelector('[data-snx-verdict]');
      const mattress = depthPanel.querySelector('[data-snx-depth-mattress]');
      const topper = depthPanel.querySelector('[data-snx-depth-topper]');
      const skirt = depthPanel.querySelector('[data-snx-depth-skirt]');
      const gap = depthPanel.querySelector('[data-snx-depth-gap]');
      const rulerLine = depthPanel.querySelector('[data-snx-depth-ruler-line]');
      const rulerText = depthPanel.querySelector('[data-snx-depth-ruler-text]');
      const min = Number(section.dataset.min) || 6;
      const max = Number(section.dataset.max) || 18;
      const FLOOR = 276;
      const PX = 9; // svg units per inch
      const MX0 = 90;
      const MX1 = 330;
      const state = { value: Number(input.value), velocity: 0, target: Number(input.value), running: false };

      function render(depth) {
        const h = depth * PX;
        const top = FLOOR - h;
        mattress.setAttribute('y', fmt(top));
        mattress.setAttribute('height', fmt(h));
        const tTop = top - 26;
        topper.setAttribute(
          'd',
          `M ${MX0 - 4} ${fmt(top)} L ${MX0 - 4} ${fmt(tTop + 10)} Q ${MX0 - 4} ${fmt(tTop)} ${MX0 + 8} ${fmt(tTop)} ` +
            `Q ${MX0 + 40} ${fmt(tTop - 8)} ${MX0 + 70} ${fmt(tTop)} Q ${MX0 + 100} ${fmt(tTop - 8)} ${MX0 + 130} ${fmt(tTop)} ` +
            `Q ${MX0 + 160} ${fmt(tTop - 8)} ${MX0 + 190} ${fmt(tTop)} Q ${MX0 + 220} ${fmt(tTop - 8)} ${MX1 - 8} ${fmt(tTop)} ` +
            `Q ${MX1 + 4} ${fmt(tTop)} ${MX1 + 4} ${fmt(tTop + 10)} L ${MX1 + 4} ${fmt(top)} Z`
        );
        // skirt reaches max inches down the side; below min it hangs loose
        const reach = Math.min(depth, max) * PX;
        const sideX = MX1 + 6;
        const endY = top + reach;
        const fits = depth <= max + 0.01;
        let d = `M ${MX1 - 6} ${fmt(top)} Q ${sideX} ${fmt(top)} ${sideX} ${fmt(top + 8)}`;
        if (depth < min) {
          // loose: wavy hang
          const slack = (min - depth) * PX;
          d += ` C ${sideX + 12} ${fmt(top + h * 0.4)} ${sideX - 6} ${fmt(top + h * 0.7)} ${sideX + 8} ${fmt(Math.min(FLOOR - 2, endY + slack * 0.4))}`;
        } else {
          d += ` L ${sideX} ${fmt(endY - (fits ? 6 : 0))}`;
          if (fits) d += ` Q ${sideX} ${fmt(endY + 2)} ${sideX - 14} ${fmt(endY + 2)} L ${sideX - 40} ${fmt(endY + 2)}`;
        }
        skirt.setAttribute('d', d);
        gap.setAttribute('d', fits ? '' : `M ${sideX + 10} ${fmt(endY)} L ${sideX + 10} ${fmt(FLOOR)}`);
        rulerLine.setAttribute('d', `M 372 ${fmt(top)} L 372 ${FLOOR} M 366 ${fmt(top)} h 12 M 366 ${FLOOR} h 12`);
        rulerText.setAttribute('x', '386');
        rulerText.setAttribute('y', fmt(top + h / 2 + 5));
        rulerText.setAttribute('text-anchor', 'start');
        rulerText.textContent = `${Math.round(depth)}"`;
      }

      function update(animate) {
        const depth = Number(input.value);
        const pct = ((depth - Number(input.min)) / (Number(input.max) - Number(input.min))) * 100;
        input.style.setProperty('--fill', `${pct}%`);
        input.setAttribute('aria-valuetext', `${depth} inches`);
        out.textContent = `${depth} in.`;
        let message = verdict.dataset.fits;
        let warn = false;
        if (depth > max) {
          message = verdict.dataset.tooDeep;
          warn = true;
        } else if (depth < min) {
          message = verdict.dataset.tooShallow;
          warn = true;
        }
        if (verdict.textContent.trim() !== message) {
          verdict.textContent = message;
          if (animate && !isReduced() && verdict.animate) {
            verdict.animate([{ transform: 'scale(.97)', opacity: 0.4 }, { transform: 'none', opacity: 1 }], { duration: 380, easing: 'cubic-bezier(.34,1.56,.64,1)' });
          }
        }
        verdict.classList.toggle('is-warn', warn);
        if (!animate || isReduced()) {
          state.value = depth;
          state.velocity = 0;
          render(depth);
        } else animateSpring(state, depth, render);
      }

      input.addEventListener('input', () => update(true));
      update(false);
    }
  }

  SR.register('fit', (root) => {
    initFit(root);
  });
})();